export type PortalOverviewPhaseStatus = 'done' | 'current' | 'pending';

export type PortalOverviewDeliveryStatus = 'inProgress' | 'pending' | 'done';

export type PortalOverviewPhase = {
  id: string;
  name: string;
  status: PortalOverviewPhaseStatus;
  startLabel: string;
  endLabel: string;
};

export type PortalOverviewDelivery = {
  id: string;
  title: string;
  dueLabel: string;
  ownerName: string;
  status: PortalOverviewDeliveryStatus;
};

export type PortalOverviewUpdate = {
  id: string;
  authorName: string;
  actionLabel: string;
  whenLabel: string;
  body: string;
  attachmentName?: string;
};

export type PortalOverviewMember = {
  id: string;
  name: string;
  role: string;
};

export type PortalOverviewData = {
  projectId: string;
  name: string;
  statusLabel: string;
  description: string;
  clientName: string;
  startLabel: string;
  endLabel: string;
  ownerName: string;
  summaryTitle: string;
  summaryBody: string;
  progressPercent: number;
  progressDeltaLabel: string;
  milestonesDone: number;
  milestonesTotal: number;
  nextDeliveryDateLabel: string;
  nextDeliveryTitle: string;
  pendingCount: number;
  pendingDetail: string;
  phases: PortalOverviewPhase[];
  deliveries: PortalOverviewDelivery[];
  updates: PortalOverviewUpdate[];
  team: PortalOverviewMember[];
};

export const portalOverviewByProjectId: Record<string, PortalOverviewData> = {
  crm: {
    projectId: 'crm',
    name: 'Implementação CRM',
    statusLabel: 'Em andamento',
    description:
      'Implantação do CRM da Baruk com foco em processos comerciais, integração com sistemas e capacitação do time.',
    clientName: 'Empresa Exemplo',
    startLabel: '01 de set. de 2026',
    endLabel: '15 de dez. de 2026',
    ownerName: 'Mariana Silva',
    summaryTitle: 'Seu projeto está dentro do prazo.',
    summaryBody:
      'Nesta semana, concluímos a integração inicial e iniciamos os testes. A próxima entrega está prevista para 12 de novembro. Não há pendências críticas no momento.',
    progressPercent: 68,
    progressDeltaLabel: '+12% desde o último mês',
    milestonesDone: 8,
    milestonesTotal: 12,
    nextDeliveryDateLabel: '12 de nov. de 2026',
    nextDeliveryTitle: 'Homologação do sistema',
    pendingCount: 3,
    pendingDetail: '2 aprovações e 1 informação',
    phases: [
      {
        id: 'phase-planning',
        name: 'Planejamento',
        status: 'done',
        startLabel: '01/09',
        endLabel: '15/09',
      },
      {
        id: 'phase-configuration',
        name: 'Configuração',
        status: 'done',
        startLabel: '16/09',
        endLabel: '10/10',
      },
      {
        id: 'phase-integration',
        name: 'Integração',
        status: 'current',
        startLabel: '11/10',
        endLabel: '05/11',
      },
      {
        id: 'phase-testing',
        name: 'Testes',
        status: 'pending',
        startLabel: '06/11',
        endLabel: '20/11',
      },
      {
        id: 'phase-uat',
        name: 'Homologação',
        status: 'pending',
        startLabel: '21/11',
        endLabel: '05/12',
      },
      {
        id: 'phase-go-live',
        name: 'Implantação',
        status: 'pending',
        startLabel: '06/12',
        endLabel: '15/12',
      },
    ],
    deliveries: [
      {
        id: 'delivery-test-env',
        title: 'Ambiente de testes configurado',
        dueLabel: '12 de nov.',
        ownerName: 'Mariana Silva',
        status: 'inProgress',
      },
      {
        id: 'delivery-customer-import',
        title: 'Importação de base de clientes',
        dueLabel: '18 de nov.',
        ownerName: 'Rafael Oliveira',
        status: 'pending',
      },
      {
        id: 'delivery-sales-training',
        title: 'Treinamento da equipe comercial',
        dueLabel: '25 de nov.',
        ownerName: 'Ana Souza',
        status: 'pending',
      },
      {
        id: 'delivery-uat',
        title: 'Homologação do sistema',
        dueLabel: '05 de dez.',
        ownerName: 'Mariana Silva',
        status: 'pending',
      },
    ],
    updates: [
      {
        id: 'u1',
        authorName: 'Mariana Silva',
        actionLabel: 'comentou',
        whenLabel: 'há 2 horas',
        body: 'Concluímos a configuração dos campos personalizados do CRM. O arquivo com o mapeamento está disponível nos documentos do projeto.',
        attachmentName: 'Configuracao_campos_CRM.pdf',
      },
      {
        id: 'u2',
        authorName: 'Rafael Oliveira',
        actionLabel: 'atualizou uma atividade',
        whenLabel: 'há 5 horas',
        body: 'Integração com ERP — status alterado para Em andamento.',
      },
      {
        id: 'u3',
        authorName: 'Ana Souza',
        actionLabel: 'adicionou um documento',
        whenLabel: 'há 1 dia',
        body: 'Manual de testes para a fase de homologação.',
        attachmentName: 'Manual_testes_homologacao.pdf',
      },
    ],
    team: [
      { id: 'm1', name: 'Mariana Silva', role: 'Gerente do projeto' },
      { id: 'm2', name: 'Rafael Oliveira', role: 'Consultor de negócios' },
      { id: 'm3', name: 'Ana Souza', role: 'Analista de implantação' },
      { id: 'm4', name: 'Carlos Mendes', role: 'Desenvolvedor' },
    ],
  },
  'app-vendedor': {
    projectId: 'app-vendedor',
    name: 'App do vendedor',
    statusLabel: 'Em andamento',
    description: 'Consulta de estoque e pedido pelo celular da equipe comercial.',
    clientName: 'Empresa Exemplo',
    startLabel: '05 de out. de 2026',
    endLabel: '08 de dez. de 2026',
    ownerName: 'Ana Ribeiro',
    summaryTitle: 'O protótipo está em validação com a equipe comercial.',
    summaryBody:
      'Três vendedores estão testando o fluxo de consulta e pedido. A próxima entrega é o piloto em uma regional.',
    progressPercent: 42,
    progressDeltaLabel: '+8% desde o último mês',
    milestonesDone: 3,
    milestonesTotal: 8,
    nextDeliveryDateLabel: '03 de nov. de 2026',
    nextDeliveryTitle: 'Protótipo com a equipe comercial',
    pendingCount: 1,
    pendingDetail: '1 informação',
    phases: [
      {
        id: 'phase-discovery',
        name: 'Descoberta',
        status: 'done',
        startLabel: '05/10',
        endLabel: '15/10',
      },
      {
        id: 'phase-prototype',
        name: 'Protótipo',
        status: 'current',
        startLabel: '16/10',
        endLabel: '03/11',
      },
      {
        id: 'phase-pilot',
        name: 'Piloto',
        status: 'pending',
        startLabel: '04/11',
        endLabel: '08/12',
      },
    ],
    deliveries: [
      {
        id: 'av-d1',
        title: 'Protótipo com a equipe comercial',
        dueLabel: '03 de nov.',
        ownerName: 'Ana Ribeiro',
        status: 'inProgress',
      },
      {
        id: 'av-d2',
        title: 'Piloto em uma regional',
        dueLabel: '08 de dez.',
        ownerName: 'Rafael Lima',
        status: 'pending',
      },
    ],
    updates: [
      {
        id: 'av-u1',
        authorName: 'Ana Ribeiro',
        actionLabel: 'atualizou uma atividade',
        whenLabel: 'há 1 dia',
        body: 'Consulta de estoque — status alterado para Em andamento.',
      },
    ],
    team: [
      { id: 'av-m1', name: 'Ana Ribeiro', role: 'Gerente do projeto' },
      { id: 'av-m2', name: 'Rafael Lima', role: 'Desenvolvedor' },
    ],
  },
};
